
import React from "react";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Divider,
  Grid,
  Chip,
  Snackbar,
  Tooltip,
  Pagination,
} from "@mui/material";

import ProductImage from "../Product/Utils/ProductImage";
import PaymentButton from "../Payment/PaymentButton";
import PaymentTimer from "./PaymentTimer";


const PAGE_SIZE = 10;

const formatTomanPrice = (price) => {
  if (price === null || price === undefined || price === "") {
    return "۰";
  }

  // Supports both numeric values and strings such as "50,000".
  // Assumes all invoice amounts returned by the API are in tomans.
  const amount = Number(String(price).replace(/,/g, "").trim());

  if (!Number.isFinite(amount)) {
    return "۰";
  }

  return Math.floor(amount).toLocaleString("fa-IR");
};

const getProductObject = (item) => {
  const raw = item?.product ?? item?.variant?.product ?? null;

  if (!raw) return {};
  if (typeof raw === "string") return { product_title: raw };
  if (typeof raw === "object") return raw;
  return {};
};

const getProductTitle = (product) => {
  if (typeof product === "string" && product.trim()) return product.trim();

  return (
    product?.product_title ||
    product?.title ||
    product?.name ||
    "محصول بدون نام"
  );
};

const getProductSlug = (product) => {
  return product?.product_slug || product?.slug || null;
};



const getProductImages = (product) => {
  if (!product || typeof product === "string") return [];

  const candidates = [
    product.product_main_image,
    product.main_image,
    product.image,
    Array.isArray(product.images) ? product.images[0] : product.images,
    Array.isArray(product.product_images)
      ? product.product_images[0]
      : product.product_images,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof candidate === "string" && candidate.trim()) {
      return [candidate.trim()];
    }

    if (typeof candidate === "object") {
      const url = candidate.image || candidate.url || candidate.src;
      if (url && String(url).trim()) return [String(url).trim()];
    }
  }

  return [];
};

const STATUS_MAP = {
  pending: { label: "در انتظار پرداخت", color: "warning" },
  paid: { label: "پرداخت شده", color: "success" },
  expired: { label: "منقضی شده", color: "error" },
  processing: { label: "در حال پردازش", color: "info" },
  shipped: { label: "ارسال شده", color: "primary" },
  delivered: { label: "تحویل داده شده", color: "success" },
  cancelled: { label: "لغو شده", color: "error" },
  refunded: { label: "بازپرداخت شده", color: "info" },
};

const SHIPMENT_STATUS_MAP = {
  pending: { label: "در انتظار پردازش مرسوله", color: "warning" },
  processing: { label: "در حال پردازش مرسوله", color: "info" },
  packed: { label: "بسته‌بندی شده", color: "info" },
  shipped: { label: "تحویل به پست/پیک", color: "primary" },
  out_for_delivery: { label: "در راه تحویل", color: "primary" },
  delivered: { label: "تحویل داده شده", color: "success" },
  failed: { label: "ارسال ناموفق", color: "error" },
  cancelled: { label: "لغو شده", color: "error" },
  returned: { label: "مرجوع شده", color: "error" },
};

const CARRIER_MAP = {
  alopeyk: "الوپیک",
  tapsi: "تپسی پک",
  tipax: "تیپاکس",
};

const getStatusDisplay = (status) => {
  const key = String(status || "").trim().toLowerCase();

  return (
    STATUS_MAP[key] || {
      label: status || "نامشخص",
      color: "default",
    }
  );
};

const getShipmentStatusDisplay = (status) => {
  const key = String(status || "").trim().toLowerCase();

  return (
    SHIPMENT_STATUS_MAP[key] || {
      label: status || "نامشخص",
      color: "default",
    }
  );
};

const getCarrierDisplay = (carrier) => {
  const key = String(carrier || "").trim().toLowerCase();

  return CARRIER_MAP[key] || carrier || "نامشخص";
};

const formatInvoiceDate = (date) => {
  if (!date) return "نامشخص";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "نامشخص";
  }

  return parsedDate.toLocaleDateString("fa-IR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default function InvoicesPageUI({
  invoices = [],
  totalCount = 0,
  currentPage = 1,
  onPageChange,
  loading = false,
  error = null,
  notification = null,
  onCloseNotification,
  onExpire,
  onNavigate,
}) {
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        dir="rtl"
      >
        <CircularProgress sx={{ color: "#ef4056" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, direction: "rtl" }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8, direction: "rtl" }}>
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={7000}
        onClose={onCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {notification ? (
          <Alert
            onClose={onCloseNotification}
            severity={notification.severity}
            sx={{
              width: "100%",
              fontWeight: 600,
              fontSize: "0.95rem",
              borderRadius: 3,
            }}
          >
            {notification.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      <Typography variant="h5" fontWeight={800} mb={4}>
        تاریخچه سفارشات
      </Typography>

      {invoices.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            textAlign: "center",
            borderRadius: 4,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography variant="h6" fontWeight={800}>
            تاریخچه سفارشات شما خالی است.
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack spacing={3}>
            {invoices.map((invoice) => {
              const rawStatus = String(invoice.status || "")
                .trim()
                .toLowerCase();

              // Backend status is authoritative.
              // A zero countdown alone does not change invoice status.
              const isPending = rawStatus === "pending";
              const isPaid = rawStatus === "paid";
              const isExpired = rawStatus === "expired";

              const hasValidRemaining =
                typeof invoice.remaining_seconds === "number" &&
                Number.isFinite(invoice.remaining_seconds);

              const statusConfig = getStatusDisplay(rawStatus);

              const locationText = [
                invoice.province,
                invoice.city,
              ]
                .filter(Boolean)
                .join("، ");

              const orderItems = Array.isArray(invoice.order_items)
                ? invoice.order_items
                : [];

              const borderColor = isPending
                ? "warning.light"
                : isExpired
                  ? "error.light"
                  : isPaid
                    ? "success.light"
                    : "grey.200";

              return (
                <Paper
                  key={invoice.id}
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor,
                    transition: "border-color 0.2s ease-in-out",
                  }}
                >
                  {/* Invoice header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                    gap={2}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        کد پیگیری سفارش {invoice.id}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        mt={0.5}
                        display="block"
                      >
                        تاریخ ثبت سفارش: {formatInvoiceDate(invoice.created_at)}
                      </Typography>
                    </Box>

                    <Chip
                      label={statusConfig.label}
                      color={statusConfig.color}
                      size="small"
                      sx={{ fontWeight: 700, borderRadius: 1.5 }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Order items */}
                  {orderItems.length > 0 ? (
                    <Stack spacing={2} mb={3}>
                      {orderItems.map((item) => {
                        const product = getProductObject(item);
                        const productTitle = getProductTitle(product);
                        const productSlug = getProductSlug(product);
                        const productImages = getProductImages(product);

                        return (
                          <Grid
                            container
                            spacing={2}
                            key={item.id}
                            alignItems="center"
                          >
                            {/* Product Images  */}
                            <Grid size={{ xs: 4, sm: 2 }}>
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: { xs: 140, sm: 140 },
                                  aspectRatio: "1 / 1",
                                  position: "relative",
                                  overflow: "hidden",
                                  borderRadius: 2,
                                }}
                              >
                                <ProductImage
                                  images={productImages}
                                  title={productTitle}
                                  disableGallery
                                />
                              </Box>
                            </Grid>

                            <Grid size={{ xs: 9, sm: 10 }}>
                              <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                onClick={() => {
                                  if (productSlug && onNavigate) {
                                    onNavigate(
                                      `/product/${encodeURIComponent(productSlug)}`,
                                    );
                                  }
                                }}
                                sx={{
                                  cursor: productSlug ? "pointer" : "default",
                                  "&:hover": productSlug
                                    ? { color: "primary.main" }
                                    : {},
                                  width: "fit-content",
                                }}
                              >
                                {productTitle}
                              </Typography>

                              <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={{ xs: 1, sm: 2 }}
                                mt={0.5}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  تعداد بسته: <b>{item.quantity ?? 0}</b>
                                </Typography>

                               

                                {item.total_units != null && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                      مجموع تعداد: <b>{item.total_units}</b>
                                  </Typography>
                                )}
                              </Box>

                              <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={{ xs: 1, sm: 2 }}
                                mt={0.75}
                              >
                                {item.price_at_purchase != null && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    قیمت واحد:{" "}
                                    <b>
                                      {formatTomanPrice(item.price_at_purchase)}
                                    </b>{" "}
                                    تومان
                                  </Typography>
                                )}

                                {item.line_total != null && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    مبلغ این کالا:{" "}
                                    <b>{formatTomanPrice(item.line_total)}</b>{" "}
                                    تومان
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          </Grid>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                      اقلامی برای این فاکتور ثبت نشده است.
                    </Alert>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Invoice footer */}
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "flex-end" }}
                    gap={3}
                  >
                   

                    {/* Pricing and payment actions */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems={{ xs: "flex-start", sm: "flex-end" }}
                      gap={2}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <Box textAlign="right">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          جمع اقلام:
                        </Typography>

                        <Typography variant="body1" fontWeight={700}>
                          {formatTomanPrice(invoice.subtotal)}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ mr: 0.5, color: "text.secondary" }}
                          >
                            تومان
                          </Typography>
                        </Typography>

                        {Number(invoice.discount_amount || 0) > 0 && (
                          <Box
                            display="flex"
                            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                            alignItems="center"
                            gap={0.5}
                            mt={0.5}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              تخفیف:
                            </Typography>

                            <Typography
                              variant="body2"
                              fontWeight={800}
                              color="success.main"
                            >
                              {formatTomanPrice(invoice.discount_amount)} تومان
                            </Typography>
                          </Box>
                        )}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mt={1}
                        >
                          مبلغ قابل پرداخت:
                        </Typography>

                        <Typography
                          variant="h6"
                          fontWeight={800}
                          color="#ef4056"
                        >
                          {formatTomanPrice(
                            invoice.total_amount ?? invoice.subtotal,
                          )}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ mr: 0.5, color: "text.secondary" }}
                          >
                            تومان
                          </Typography>
                        </Typography>
                      </Box>

                      {/* Pending invoices */}
                      {isPending && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems={{ xs: "flex-start", sm: "flex-end" }}
                          gap={1.5}
                        >
                          {hasValidRemaining &&
                            invoice.remaining_seconds > 0 && (
                              <PaymentTimer
                                remainingSeconds={invoice.remaining_seconds}
                                onExpire={() => onExpire?.(invoice.id)}
                              />
                            )}

                          <PaymentButton invoiceId={invoice.id} />
                        </Box>
                      )}

                      {isPaid && (
                        <Chip
                          label="سفارش پرداخت شد"
                          color="success"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 2 }}
                        />
                      )}

                      {isExpired && (
                        <Chip
                          label="مهلت پرداخت منقضی شده"
                          color="error"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 700, borderRadius: 2 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Stack>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={Math.min(Math.max(currentPage, 1), totalPages)}
                onChange={(_, page) => onPageChange?.(page)}
                color="primary"
                shape="rounded"
                dir="ltr"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
}
